import { reducerWithInitialState } from 'typescript-fsa-reducers'

import * as actions from './actions'
import * as apiActions from '../api/actions'
import * as settingsActions from '../settings/actions'
import { KeypadItem, KeypadItemError, KeypadEntryStatus } from './types'
import { loadScreen } from '../screen/actions'
import { loadCollectionDetails } from '../collectionlist/actions'
import { getKeypadStatus, isLoading } from './functions'

export interface StoreState {
	readonly items: KeypadItem[]
  readonly error?: KeypadItemError
	readonly keypadEntryId?: number
	readonly keypadEntryStatus?: KeypadEntryStatus
	readonly loading: boolean
}

const INITIAL_STATE: StoreState = {
	items: [],
  error: undefined,
	keypadEntryId: undefined,
	keypadEntryStatus: undefined,
	loading: false,
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	.case(actions.loadedKeypadItemsComplete, (state, payload): StoreState => {
		const loading = isLoading(payload)
		return {
			...state,
      items: payload,
      error: undefined,
			loading,
			keypadEntryStatus: loading ? KeypadEntryStatus.Processing : undefined,
		}
	})
  .case(actions.loadedKeypadItemsNotFound, (state, payload): StoreState => {
		return {
			...state,
      items: [],
      error: payload,
			loading: false,
		}
	})
	.case(actions.cleanKeypadItems, (state): StoreState => {
		return {
			...state,
			keypadEntryId: undefined,
			keypadEntryStatus: undefined,
      items: [],
      error: undefined,
			loading: false,
		}
	})
	// keypad entry
	.case(settingsActions.loadItemOnKeypadEntry.started, (state) => {
		return {
			...state,
			keypadEntryStatus: KeypadEntryStatus.Processing,
      items: [],
      error: undefined,
			loading: true,
		}
	})
	.case(settingsActions.loadItemOnKeypadEntry.done, (state) => {
		return {
			...state,
			keypadEntryStatus: undefined,
		}
	})
	.case(settingsActions.loadItemOnKeypadEntry.failed, (state) => {
		return {
			...state,
			keypadEntryId: undefined,
			keypadEntryStatus: KeypadEntryStatus.Failed,
		}
	})
	.cases([loadScreen, loadCollectionDetails], (state, payload) => {
		// saves the id if any load for a keypad entry item has started
		if (state.keypadEntryStatus === KeypadEntryStatus.Processing) {
			return {
				...state,
				keypadEntryId: payload.id,
			}
		}
		// return state unchanged
		return state
	})
	// update downloaded screens/collections
	.case(apiActions.fetchScreen.done, (state, payload): StoreState => {
		const { items } = state

		items.forEach(item => {
			if (item.id === payload.result.id) {
				item.title = payload.result.title
				item.subtype = payload.result.screen_type
				item.icon = payload.result.cover_image_grid || payload.result.cover_image
			}

			return item
		})

		const loading = isLoading(items)

		return {
			...state,
			items: [ ...items ],
			loading,
			keypadEntryStatus: getKeypadStatus(loading, items),
		}
	})
	.case(apiActions.fetchScreen.failed, (state, payload): StoreState => {
		const { items } = state

		// remove screen from items array
		const newItems = items.filter(item => item.id !== payload.params.screenId)
		const loading = isLoading(newItems)

		return {
			...state,
			items: [ ...newItems ],
			loading,
			keypadEntryStatus: getKeypadStatus(loading, newItems, true),
		}
	})
	.case(apiActions.fetchCollection.done, (state, payload): StoreState => {
		const { items } = state

		items.forEach(item => {
			if (item.id === payload.result.id) {
				item.title = payload.result.title
				item.subtype = payload.result.subtype
				item.icon = payload.result.cover_image_grid || payload.result.cover_image
			}

			return item
		})

		const loading = isLoading(items)

		return {
			...state,
			items: [ ...items ],
			loading,
			keypadEntryStatus: getKeypadStatus(loading, items),
		}
	})
	.case(apiActions.fetchCollection.failed, (state, payload): StoreState => {
		const { items } = state

		// remove screen from items array
		const newItems = items.filter(item => item.id !== payload.params.collectionId)
		const loading = isLoading(newItems)

		return {
			...state,
			items: [ ...newItems ],
			loading,
			keypadEntryStatus: getKeypadStatus(loading, newItems, true),
		}
	})
